import { OWObjectList, OWObjectListItem } from "OW.Remoting/Remoting/DataTypes/OWObjectList";
import { ErrorHandler } from "UI/Errors";
import { InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { BasicDataType } from "../../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { ActionContext, ActionType } from "../../../../UI.Controls/Actions/IAction";
import { DownloadSelectBuilder, DownloadSelectBuilderOption } from "../../DataBuilders/DownloadSelectBuilder";
import { DownloadManager } from "../../DownloadManager";
import { MessageButtonType, MessageWindow } from "../../MessageWindow";
import { IFrameWithProfile, IMasterableFrameWithDataGrid } from "../Frames/FrameInterfaces";
import { IEditableFrame } from "../Frames/IEditableFrame";
import { StandardAction } from "./StandardAction";
/**
 * Akce pro hromadné stahování souborů z datagridu
 */
export class BatchDownloadAction extends StandardAction {
    private readonly _enhancedOwner: IMasterableFrameWithDataGrid & IFrameWithProfile;
    private _recalculateListener?: () => void;

    /**
     * Vytvoří akci a napojí ji na frame
     * @param frame Vlastnící frame s datagridem a profily
     */
    constructor(frame: IMasterableFrameWithDataGrid & IFrameWithProfile) {
        super(ActionType.standardDownloadAll, () => {
            const documentColumns = frame.currentDataGrid.columns.filter(c => c.dataType === BasicDataType[BasicDataType.Document] && c.visible);
            if (documentColumns.length === 0)
                throw new InvalidOperationException("No visible document column");

            // dokončí se až když uživatel potvrdí nebo zamítne stahování (příprava může chvíli trvat), aby nemohl spustit více stahování najednou bez doběhnutí přípravy
            // a potřebných kontrol (třeba na maximální počet souběžných stahování, který se řeší až při skutečném začátku stahování)
            return new Promise<void>(async (resolve, reject) => {
                let selectedAttributes: string[];
                if (documentColumns.length === 1) // když existuje jeden sloupec s dokumenty, tak se hned začne stahovat
                    selectedAttributes = documentColumns.map(c => c.attrName);
                else // jinak se nechá uživatele zvolit sloupce
                    selectedAttributes = await BatchDownloadAction._showUserDownloadSelectDialog(documentColumns.map<DownloadSelectBuilderOption>(c => ({ attrName: c.attrName, titleLocKey: c.locKey, titleLocParams: c.locParams })));

                if (!selectedAttributes || selectedAttributes.length === 0) {
                    resolve();
                    return;
                }

                if (frame.currentDataGrid.selectedRows.length > 0) {
                    const objList = new OWObjectList();
                    objList.className = frame.objectName;
                    objList.isSelectAll = frame.currentDataGrid.isSelectedAll;
                    objList.readVersioned = this.ownerForm.readVersioned;

                    if (objList.isSelectAll) {
                        objList.condition = frame.currentDataGrid.dataSource.condition;
                        objList.conditionParams = frame.currentDataGrid.dataSource.conditionParams;
                        objList.items = frame.currentDataGrid.dataRows.filter(row => !row.isSelected).map(row => new OWObjectListItem({ id: row.dataKey }, objList.className));
                    } else {
                        objList.items = frame.currentDataGrid.selectedRows.map(row => new OWObjectListItem({ id: row.dataKey }, objList.className));
                    }

                    DownloadManager.downloadFiles(objList, selectedAttributes, () => resolve()).catch(e => reject(e));
                } else {
                    resolve();
                }
            }).catch(e => ErrorHandler.instance.handleError(e));
        }, undefined, ActionContext.frameAction, undefined, frame);

        this._enhancedOwner = frame;
        this.enabled = true;
        this.enabledVisual = true;

        this._events.add(frame.dataGridInitialized, () => {
            const documentColumns = this._getDocumentColumns();
            if (documentColumns.length === 0) // žádný sloupec není typu dokument
                return;

            this._recalculateListener = () => this._recalculateVisibility();

            // viditelnost akce je potřeba přehodnotit při:
            // změně profilu (skrytí/zviditelnění sloupce)
            this._events.add(frame.frameProfileChanged, this._recalculateListener);
            // byl vybrán jiný profil
            this._events.add(frame.selectedProfileChanged, this._recalculateListener);
            // změně výběru (žádný/jeden/více)
            this._events.add(frame.selectionChanged, this._recalculateListener);
            // změně režimu editace (vstup/ukončení)
            if (this._enhancedOwner instanceof IEditableFrame)
                this._events.add(this._enhancedOwner.editModeChanged, this._recalculateListener);
        });
    }

    private _getDocumentColumns(): { visible: boolean }[] {
        return this._enhancedOwner.currentDataGrid.columns != null ? this._enhancedOwner.currentDataGrid.columns.filter(c => c.dataType === BasicDataType[BasicDataType.Document]) : [];
    }

    private _getVisibleColumnCount(): number {
        return this._getDocumentColumns().filter(c => c.visible).length;
    }

    private _recalculateVisibility(): void {
        if (this._enhancedOwner instanceof IEditableFrame && this._enhancedOwner.isEditing) {
            this.visible = false;
            return;
        }

        // pokud není označený žádný záznam
        const selectedRowsCount = this._enhancedOwner.currentDataGrid.selectedRows.length;
        if (selectedRowsCount <= 0) {
            this.visible = false;
            return;
        }

        // akce má být viditelná v případě, kdy je "označeno" více dokumentů (tedy více v jednom řádku nebo alespoň jeden ve více řádcích)
        const visibleColumns = this._getVisibleColumnCount();
        if (visibleColumns > 1 || (visibleColumns === 1 && selectedRowsCount > 1))
            this.visible = true;
        else
            this.visible = false;
    }

    /**
     * Zobrazí okno pro výběr souborů ke stažení
     * @param options Kolekce atributů a jejich názvů, které se mají nabídnout k výběru
     */
    private static async _showUserDownloadSelectDialog(options: DownloadSelectBuilderOption[]): Promise<string[] | null> {
        const mw = new MessageWindow();
        mw.header = "GUI-666836"; // Vyberte dokumenty ke stažení
        mw.okButtonCaption = "GUI-666839"; // Stáhnout
        mw.cancelButtonCaption = "GUI-33682"; // Storno
        const content = new DownloadSelectBuilder(mw, options, "GUI-303572"/* Vybrat vše */, "GUI-669074"/* Zrušit výběr */);
        mw.contentBuilder = content;

        const result = await mw.show();
        if (result.result !== MessageButtonType.ok)
            return null;

        return content.selectedAttributes;
    }
}
